import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Add } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { createInstitutionalTci } from "services/fde/fdeService";
import {
  TCI_PHASE_OPTIONS,
  groupTciRegistryRows,
  listTciRegistry,
} from "services/fde/fdeRegistryService";
import { derivePrimaryProducts } from "constants/productSurfaces";
import ProductSurfacesField from "components/fde/ProductSurfacesField";
import { isFirestorePermissionDenied } from "utils/firestoreErrors";

const EMPTY_CREATE_FORM = {
  name: "",
  phase: "Assessment",
  productSurfaces: [],
  promptIdPattern: "",
  aiCeiling: 0.5,
  aiCeilingWithCmr: 0.65,
};

function renderGroupedMenuItems({ platform, institutional }) {
  const items = [];
  if (platform.length) {
    items.push(
      <ListSubheader key="platform-header">Platform TCIs</ListSubheader>,
      ...platform.map((tci) => (
        <MenuItem key={tci.id} value={tci.id}>
          {tci.name || tci.id}
        </MenuItem>
      )),
    );
  }
  items.push(
    <ListSubheader key="institutional-header">Your institution&apos;s TCIs</ListSubheader>,
    ...(institutional.length
      ? institutional.map((tci) => (
          <MenuItem key={tci.id} value={tci.id}>
            {tci.name || tci.id}
          </MenuItem>
        ))
      : [
          <MenuItem key="institutional-empty" disabled value="__none__">
            No institutional TCIs yet
          </MenuItem>,
        ]),
  );
  return items;
}

export function useTciRegistryOptions({ institutionId, enabled = true } = {}) {
  const [tcis, setTcis] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!enabled) {
      setTcis([]);
      setLoading(false);
      return [];
    }
    setLoading(true);
    setError(null);
    try {
      const rows = await listTciRegistry({ institutionId });
      setTcis(rows);
      return rows;
    } catch (err) {
      if (!isFirestorePermissionDenied(err)) {
        setError(err?.message || "Failed to load TCIs.");
      }
      setTcis([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [enabled, institutionId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const grouped = useMemo(() => groupTciRegistryRows(tcis), [tcis]);
  const tciNameById = useMemo(() => {
    const map = new Map();
    tcis.forEach((tci) => map.set(tci.id, tci.name || tci.id));
    return map;
  }, [tcis]);

  return { tcis, grouped, tciNameById, loading, error, reload };
}

export function TciRegistrySelect({
  value,
  onChange,
  institutionId,
  multiple = false,
  label = "TCIs",
  allowCreate = true,
  disabled = false,
  error = false,
  required = false,
}) {
  const { grouped, loading, error: loadError, reload } = useTciRegistryOptions({
    institutionId,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: EMPTY_CREATE_FORM });

  const menuItems = useMemo(
    () => renderGroupedMenuItems(grouped),
    [grouped],
  );

  const onCreate = async (values) => {
    if (!institutionId) return;
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createInstitutionalTci({
        institutionId,
        name: values.name.trim(),
        phase: values.phase,
        productSurfaces: values.productSurfaces || [],
        primaryProducts: derivePrimaryProducts(values.productSurfaces || []),
        promptIdPattern: values.promptIdPattern.trim(),
        aiCeiling: Number(values.aiCeiling),
        aiCeilingWithCmr: Number(values.aiCeilingWithCmr),
      });
      await reload();
      if (multiple) {
        const current = Array.isArray(value) ? value : [];
        onChange([...current, result.tciId]);
      } else {
        onChange(result.tciId);
      }
      setCreateOpen(false);
      reset(EMPTY_CREATE_FORM);
    } catch (err) {
      setCreateError(err?.message || "Failed to create institutional TCI.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      {loadError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {loadError}
        </Alert>
      ) : null}
      <FormControl fullWidth error={error} disabled={disabled || loading}>
        <InputLabel id="tci-registry-select-label">{label}</InputLabel>
        <Select
          labelId="tci-registry-select-label"
          label={label}
          multiple={multiple}
          required={required}
          value={value || (multiple ? [] : "")}
          onChange={(event) => onChange(event.target.value)}
          onOpen={() => {
            reload();
          }}
          renderValue={
            multiple
              ? (selected) =>
                  selected.map((id) => grouped.platform.concat(grouped.institutional).find((row) => row.id === id)?.name || id).join(", ")
              : undefined
          }
        >
          {menuItems}
        </Select>
      </FormControl>
      {allowCreate && institutionId ? (
        <Button
          size="small"
          startIcon={<Add />}
          sx={{ mt: 1 }}
          onClick={() => {
            setCreateError(null);
            reset(EMPTY_CREATE_FORM);
            setCreateOpen(true);
          }}
        >
          Create new TCI
        </Button>
      ) : null}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create institutional TCI</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
          <Typography variant="body2" color="text.secondary">
            Institutional TCIs are private to your organization and are not shared in the
            cross-institution platform catalog.
          </Typography>
          {createError ? (
            <Alert severity="error" onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          ) : null}
          <TextField
            label="TCI name"
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
            {...register("name", { required: "Name is required." })}
          />
          <Controller
            name="phase"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="create-tci-phase-label">Phase</InputLabel>
                <Select {...field} labelId="create-tci-phase-label" label="Phase">
                  {TCI_PHASE_OPTIONS.map((phase) => (
                    <MenuItem key={phase} value={phase}>
                      {phase}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          <Controller
            name="productSurfaces"
            control={control}
            render={({ field }) => (
              <ProductSurfacesField value={field.value || []} onChange={field.onChange} />
            )}
          />
          <TextField
            label="Prompt ID pattern"
            fullWidth
            placeholder="*-custom-workflow-*"
            error={Boolean(errors.promptIdPattern)}
            helperText={errors.promptIdPattern?.message}
            {...register("promptIdPattern", { required: "Prompt ID pattern is required." })}
          />
          <TextField
            label="AI support potential"
            type="number"
            inputProps={{ min: 0, max: 1, step: 0.01 }}
            {...register("aiCeiling", { required: true, valueAsNumber: true })}
          />
          <TextField
            label="AI support potential with MasterChart"
            type="number"
            inputProps={{ min: 0, max: 1, step: 0.01 }}
            {...register("aiCeilingWithCmr", { required: true, valueAsNumber: true })}
          />
          <Typography variant="body2" color="text.secondary">
            Modeled share of this interaction AI can prepare for a clinician to review. The
            clinician stays responsible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <LoadingButton variant="contained" loading={creating} onClick={handleSubmit(onCreate)}>
            Create TCI
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TciRegistrySelect;
